import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToasts } from 'common/toast';
import { PlannerTable } from './PlannerTable';
import { useCultureHubQueryParams } from '../../../hooks/useCultureHubQueryParams';
import { useGetActionPlansQuery } from '../../../api/cultureHubApi';
import { useApiPortalV1AssessmentsPlansInterventionsDestroyMutation } from '@/store/generated/portal/assessments/plans-interventions';
import { useInterventionsPlanSync } from '../../../hooks/useInterventionsPlanSync';
import { getPlan, removeRecommendationFromPlan } from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { isCurrentMemberTeamLeader, getMostRecentAssessmentPeriodId, getLeadershipTeams } from '@/common/selectors/auth';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { StyledAddButton } from '@/common/components/button/StyledButton';
import { PageContent, PageHeader, PageSection } from '@/common/styles/page';

export const FocusPlanTab: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const plan = useSelector(getPlan);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const mostRecentAssessmentPeriodId = useSelector(getMostRecentAssessmentPeriodId);

  const { assessmentPeriodId, teamId, organizationId, shouldFetchData } = useCultureHubQueryParams();
  const queryTeamId = teamId;
  const leadershipTeams = useSelector(getLeadershipTeams);
  const teamName = leadershipTeams?.find(team => team.id === queryTeamId)?.name;

  const shouldFetchActionPlans = !!assessmentPeriodId && !!shouldFetchData && !!organizationId && !!queryTeamId;

  const { data: actionPlansData, isLoading: actionPlansLoading, isFetching: actionPlansFetching, refetch: refetchActionPlans } = useGetActionPlansQuery(
    { organizationId: organizationId!, assessmentPeriodId: assessmentPeriodId!, teamId: queryTeamId },
    {
      skip: !shouldFetchActionPlans,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const [deletePlan] = useApiPortalV1AssessmentsPlansInterventionsDestroyMutation();

  const existingActionPlans = (actionPlansData ?? []).length > 0 ? (actionPlansData as any[]) : undefined;

  useInterventionsPlanSync(existingActionPlans, assessmentPeriodId, queryTeamId);

  const getPlanItemTeamId = (item: any): number | undefined => {
    if (typeof item.team_id === 'number') return item.team_id;
    if (typeof item.team_id === 'string' && /^\d+$/.test(item.team_id)) return Number(item.team_id);
    if (item.team && typeof item.team === 'object' && typeof item.team.id === 'number') return item.team.id;
    if (item.team && typeof item.team === 'object' && typeof item.team.id === 'string' && /^\d+$/.test(item.team.id)) return Number(item.team.id);
    return undefined;
  };

  const planForTeam = queryTeamId == null ? plan : plan.filter((item: any) => getPlanItemTeamId(item) === queryTeamId);

  const hasFetchStarted = !!assessmentPeriodId && !!shouldFetchData && !!organizationId && !!queryTeamId;
  const isLoading = actionPlansLoading || actionPlansFetching;
  const isMostRecent = assessmentPeriodId === mostRecentAssessmentPeriodId;
  const addToFocusPlanDisabledReason = !isMostRecent
    ? 'It is only possible to add to the current assessment period.'
    : undefined;

  const handleRemove = async (identifier: string | number | object) => {
    const matchedItem = (() => {
      if (typeof identifier === 'object' && identifier !== null) {
        const rec = identifier as any;
        return plan.find((p: any) => p.recommendation_template_id === rec.recommendation_template_id && (p as any).task_index === (rec.task_index ?? 0));
      }
      return plan.find((p: any) => (p as any).task_id === identifier || p.recommendation_template_id === identifier || (p as any).action_plan_id === identifier);
    })();

    if (matchedItem && (matchedItem as any).action_plan_id) {
      try {
        await deletePlan({ id: (matchedItem as any).action_plan_id }).unwrap();
        addToast('Focus area removed', { appearance: 'success', autoDismiss: true });
        await refetchActionPlans();
      } catch (err: any) {
        console.error('Failed to delete action plan record:', err);
        if (err && err.status === 405) {
          addToast('Failed to remove focus area: server does not support deletion (405).', { appearance: 'error', autoDismiss: true });
        } else {
          addToast('Failed to remove focus area. Please try again.', { appearance: 'error', autoDismiss: true });
        }
        return;
      }
    }

    dispatch(removeRecommendationFromPlan(identifier));

    if (!(matchedItem && (matchedItem as any).action_plan_id)) {
      addToast('Focus area removed', { appearance: 'success', autoDismiss: true });
    }
  };

  if (!hasFetchStarted) {
    return <LoadingSpinner />;
  }

  return (
    <PageContent>
      <PageSection padleft padright>
        <PageSection padtop>
          <div className="flex items-center justify-between mb-2">
            <PageHeader
              title={`Focus Plan${teamName ? ` for ${teamName}` : ''}`}
              caption="Track your team’s current focus areas and manage your plan."
              controls={
                <span title={addToFocusPlanDisabledReason} style={{ display: 'inline-block' }}>
                  <StyledAddButton
                    width="160px"
                    label="Add to Focus Plan"
                    disabled={!isMostRecent}
                    onClick={() => navigate('/app/culture-hub/interventions/focus-plan-recommendations')}
                  />
                </span>
              }
            />
          </div>
        </PageSection>

        <PlannerTable
          items={planForTeam}
          isLoading={isLoading}
          onRemove={handleRemove}
          hasExistingActionPlans={!!existingActionPlans?.length}
          isMostRecent={isMostRecent}
        />
      </PageSection>
    </PageContent>
  );
};
